import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty, map, orderBy, size } from "lodash";
import { createErrorSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { getPendingLinkedEmployers } from "actions/adminActions";
import { formatCurrency } from "utils/number";
import { FiUsers } from "react-icons/fi";

import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import IconMessageCard from "components/IconMessageCard";
import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import Alert from "components/Alert";
import type { RootState } from "types/state";
import type { GetPendingAndLinkedEmployersQuery } from "types/graphql";

const errorSelector = createErrorSelector(
  adminConstants.GET_PENDING_LINKED_EMPLOYERS
);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getPendingLinkedEmployers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  userId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

type PendingAndLinkedEmployers = NonNullable<
  GetPendingAndLinkedEmployersQuery["getPendingAndLinkedEmployers"]
>;

/** Non-null pending-employer element from the query selection. */
type PendingEmployer = NonNullable<
  NonNullable<PendingAndLinkedEmployers["pendingEmployers"]>[number]
>;

/** Non-null linked-employer element from the query selection. */
type LinkedEmployer = NonNullable<
  NonNullable<PendingAndLinkedEmployers["linkedEmployers"]>[number]
>;

/** Success-action shape resolved by the getPendingLinkedEmployers thunk. */
interface PendingLinkedEmployersResult {
  data: PendingAndLinkedEmployers;
}

interface State {
  isFetching: boolean;
  pendingEmployers: PendingEmployer[];
  linkedEmployers: LinkedEmployer[];
}

/** Row produced by _getEmployerLinksData and consumed by the table columns. */
interface EmployerLinkRow {
  id: string;
  companyName: string | null;
  companyId: string;
  groupName: string;
  groupId: string;
  employerStatus: string;
  contributionAmount: string;
}

class EmployerLinks extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isFetching: true,
      pendingEmployers: [],
      linkedEmployers: [],
    };
  }

  async componentDidMount() {
    // dispatchFullLifecycle resolves to a success | failure action union; the
    // original code reads the success `.data` shape directly — cast to it.
    const { data: pendingAndLinkedEmployers } =
      (await this.props.getPendingLinkedEmployers(
        this.props.client as ApolloClient<object>,
        this.props.userId as string
      )) as unknown as PendingLinkedEmployersResult;

    this.setState({
      pendingEmployers:
        pendingAndLinkedEmployers.pendingEmployers as PendingEmployer[],
      linkedEmployers: orderBy(pendingAndLinkedEmployers.linkedEmployers, [
        "deleted",
        "asc",
      ]) as LinkedEmployer[],
      isFetching: false,
    });
  }

  _getEmployerLinksData = (): EmployerLinkRow[] => {
    const linkedRows = map(this.state.linkedEmployers, (linkedEmployer) => {
      const { company, group, contribution, deleted } = linkedEmployer;
      const status = deleted ? "INACTIVE" : "ACTIVE";
      const contributionAmount = get(contribution, "amount", 0);

      return {
        id: linkedEmployer.id,
        // company/group are nullable in the schema but the original code
        // dereferences them directly — preserve the unconditional access.
        companyName: company!.name,
        companyId: company!.id,
        groupName: group!.name,
        groupId: group!.id,
        employerStatus: status,
        contributionAmount: formatCurrency(contributionAmount),
      };
    });
    const linkRequestRows = map(
      this.state.pendingEmployers,
      (pendingEmployer) => {
        const { company, group, contribution, status } = pendingEmployer;

        const contributionAmount = get(
          contribution,
          "contributionChangeRequests.0.userNewContributionAmount",
          0
        );

        return {
          id: pendingEmployer.id,
          // group is nullable in the schema but the original code dereferences
          // it directly — preserve the unconditional access.
          companyName: company.name,
          companyId: company.id,
          groupName: group!.name,
          groupId: group!.id,
          employerStatus: status,
          contributionAmount: formatCurrency(contributionAmount),
        };
      }
    );

    return [...linkedRows, ...linkRequestRows];
  };

  _buildEmployerLinksColumns = (): Array<IconTableColumn<EmployerLinkRow>> => {
    const columns: Array<IconTableColumn<EmployerLinkRow>> = [
      {
        label: "Company Name",
        key: "companyName",
      },

      {
        label: "Company ID",
        key: "companyId",
      },
      {
        label: "GroupName",
        key: "groupName",
      },
      {
        label: "Group Id",
        key: "groupId",
      },

      {
        label: "Status",
        key: "employerStatus",
      },
      {
        label: "Contribution Amount",
        key: "contributionAmount",
      },
      {
        customComponent: (props: EmployerLinkRow) => {
          return (
            <div>
              <Button
                color="action"
                name="action"
                onClick={() => this.props.push(`/employers/${props.companyId}`)}
                btnLabel="View Employer"
                size={"sm"}
              />
            </div>
          );
        },
      },
    ];
    return columns;
  };

  render() {
    const columns = this._buildEmployerLinksColumns();
    const data = this._getEmployerLinksData();

    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }
    const { pendingEmployers, linkedEmployers } = this.state;
    const noEmployers = isEmpty(pendingEmployers) && isEmpty(linkedEmployers);

    if (noEmployers) {
      return (
        <IconMessageCard
          header={"Linked Employers"}
          subheader="User has no linked employers."
          icon={<FiUsers color="white" stroke="#60A4BF" size={16} />}
        />
      );
    }

    return (
      <div id={"admin-user-employers"}>
        <IconTableHeader
          tableHeader="Linked Employers"
          tableCount={size(this.state.linkedEmployers)}
        />
        <IconTable columns={columns} data={data} />
      </div>
    );
  }
}

export default connector(withApollo<Props>(EmployerLinks));
